import { useEffect, useRef, useState } from 'react';
import '../styles/landing.css';

/**
 * Landing — the signed-out first impression (Jonathan email 2026-10-07).
 *
 * Four beats of Jonathan's own copy, one per screen-height section, revealed
 * as they enter the viewport. The last beat hands over to the real screen
 * beneath it: the "Step Inside" pill (founder email 2026-10-20, card
 * Ox8ETAzhmkIy — was "Enter Pear", which was itself "Step inside" before)
 * scrolls the Pair form into view rather than switching routes — the SPA,
 * its deep links and every walk script that waits for #pair-screen keep
 * working exactly as before. The tagline "Two of you. One space to remember."
 * now sits under the final line here, unbolded, rather than leading the Pair
 * screen.
 *
 * Above the fold: one "Sign in / Sign up" button (same card) now lives in the
 * App HEADER, on the same line as the "Pear" wordmark (founder email
 * 2026-10-25, card 5RluBbgTSKCs) — App.tsx renders it and calls the exported
 * signIn() below. It scrolls to the Pair form AND opens the existing
 * sign-in form — the Pair screen owns the auth state, so the click reaches
 * #btn-go-signin rather than duplicating the form. Signed in already (or the
 * form is open), that target is gone and the button degrades to a plain scroll.
 *
 * Motion is one IntersectionObserver + a CSS transition. No library, no
 * parallax, and prefers-reduced-motion gets the copy with no travel at all.
 */

interface Beat {
  id: string;
  /** Eyebrow above the line — small, tracked, mustard. */
  kicker?: string;
  line: string;
  /** Second line, smaller, cream at 70%. */
  sub?: string;
}

/* No kicker on beat-one: the shell header already carries the wordmark, and an
 * eyebrow spelling "Pear" again above a headline that opens with "Pear" is the
 * duplicate Jonathan flagged (card RfH0OR9xTQmF). The header stays the single
 * brand instance; his copy keeps its own opening. */
const BEATS: Beat[] = [
  {
    id: 'beat-one',
    line: 'Pear is a place for you and your person.',
  },
  {
    id: 'beat-two',
    line: 'Pear assists you in matters of the heart and the home.',
  },
  {
    id: 'beat-three',
    line: 'Pear is a place for your plans, dreams and the movie you both want to watch tonight.',
  },
];

const ENTER_ID = 'pair-screen';

function enter(): void {
  document.getElementById(ENTER_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/** Top-of-page sign in / sign up (card Ox8ETAzhmkIy, re-laid-out by Jon's
 *  email 2026-10-25, card 5RluBbgTSKCs): scroll to the Pair form, then open
 *  the EXISTING sign-in form by clicking #btn-go-signin — Pair.tsx owns
 *  authMode, so the button stays a thin forwarder. The target only renders
 *  while signed out with no auth form open; otherwise this is a scroll,
 *  which is still the right outcome.
 *
 *  The pill itself now lives in the App HEADER, inline with the "Pear"
 *  wordmark — Jon: "sign up pill in line with the pear text". App.tsx
 *  imports this handler so the button is one forwarder wherever it sits. */
export function signIn(): void {
  /* Reduced motion is a request about ANIMATION, not only about CSS — same
     reasoning as learnMore: scrollIntoView({behavior:'smooth'}) keeps running
     after the stylesheet honours the preference. Instant jump instead. */
  const reduce = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.getElementById(ENTER_ID)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  document.getElementById('btn-go-signin')?.click();
}

/**
 * The product overview behind "Learn more" (card ncBQLIv52l5w, from the sol
 * review). Two groups, in Jonathan's order: Pear is a PRIVATE SPACE first
 * (the things that belong to the two of you), then a RELATIONSHIP CONCIERGE
 * (the things Pear does for you). Copy stays in the concierge voice — plain,
 * warm, no feature-matrix speak.
 */
interface OverviewGroup {
  id: string;
  kicker: string;
  line: string;
  items: { name: string; blurb: string }[];
}

const OVERVIEW: OverviewGroup[] = [
  {
    id: 'overview-private',
    kicker: 'A private space',
    line: 'Everything here belongs to the two of you. No feed, no audience, no one else.',
    items: [
      { name: 'Memories', blurb: 'Save a photo, a link or a passing thought — it lands in one place you both keep.' },
      { name: 'Questions', blurb: 'One good question a day. Answer separately, open it together.' },
      { name: 'Games', blurb: 'Small rounds for a quiet night — this or that, guess the place, who knows who better.' },
      { name: 'Plans', blurb: 'The movie you both want to watch, the trip you keep talking about. Held until you get to it.' },
    ],
  },
  {
    id: 'overview-concierge',
    kicker: 'A relationship concierge',
    line: 'At your service — noticing the admin and the occasions so you do not have to hold them in your head.',
    items: [
      { name: 'Gifts', blurb: 'It learns what they like and nudges you ahead of the date with a set of thoughtful recommendations.' },
      { name: 'Dates', blurb: 'Suggestions that fit your budget, your area and your week — never a nag, always an offer.' },
      { name: 'Admin', blurb: 'The shared inbox, the chores, the renewals — triaged into the thread as small cards. The weekly meeting is transcribed, and its reminders land in your calendar and tasks.' },
      /* Founder email 2026-10-19 (second batch): the digest copy is his own
         wording — the short note covers the week past and the one ahead, and
         the briefing is no longer flagged as editable here. Landing copy
         only; the digest runtime ships separately. */
      { name: 'The weekly digest', blurb: 'Two editions that reflect life together: a short note on the week past and the one ahead, and a curated briefing on your partner\u2019s interests to enrich your conversations.' },
    ],
  },
];

const OVERVIEW_ID = 'landing-overview';

function learnMore(): void {
  /* Reduced motion is a request about ANIMATION, not only about CSS
     (pre-deploy review, 2026-10-17): `behavior: 'smooth'` is a programmatic
     animation and keeps running when the stylesheet has already honoured the
     preference. Instant jump instead. */
  const reduce = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.getElementById(OVERVIEW_ID)?.scrollIntoView({
    behavior: reduce ? 'auto' : 'smooth',
    block: 'start',
  });
}

export function Landing() {
  const rootRef = useRef<HTMLElement | null>(null);
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-beat]'));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      setRevealed(new Set(nodes.map(n => n.dataset.beat!)));
      return;
    }

    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const key = (entry.target as HTMLElement).dataset.beat;
          if (key && entry.isIntersecting) {
            setRevealed(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
            io.unobserve(entry.target);
          }
        }
      },
      // Fire early — a beat should already be settled when it lands centre.
      { rootMargin: '0px 0px -25% 0px', threshold: 0.15 },
    );

    nodes.forEach(n => io.observe(n));
    return () => io.disconnect();
  }, []);

  return (
    <section className="pear-landing" id="landing" aria-label="Pear — introduction" ref={rootRef}>
      {/* The "Sign in / Sign up" door moved UP into the App header, on the
          same line as the "Pear" wordmark (founder email 2026-10-25, card
          5RluBbgTSKCs) — its own row above the beats is gone. App.tsx renders
          it, this file owns the handler (signIn above). */}
      {BEATS.map((beat, i) => (
        <div
          key={beat.id}
          className={`landing-beat${revealed.has(beat.id) ? ' is-in' : ''}`}
          id={beat.id}
          data-beat={beat.id}
          data-beat-index={i}
        >
          {beat.kicker && <p className="landing-kicker">{beat.kicker}</p>}
          <p className="landing-line">{beat.line}</p>
          {beat.id === 'beat-three' && (
            <button
              type="button"
              className="landing-learn"
              id="btn-landing-learn-more"
              onClick={learnMore}
            >
              Learn more
            </button>
          )}
        </div>
      ))}

      {/* The product overview the "Learn more" pill opens — two groups in
          concierge voice, private space first, concierge second
          (card ncBQLIv52l5w). Each group is a data-beat so it reveals on
          scroll like every other beat. */}
      <div className="landing-overview" id={OVERVIEW_ID} aria-label="What Pear is">
        {OVERVIEW.map(group => (
          <section
            key={group.id}
            className={`landing-group${revealed.has(group.id) ? ' is-in' : ''}`}
            id={group.id}
            data-beat={group.id}
          >
            <p className="landing-kicker">{group.kicker}</p>
            <p className="landing-group-line">{group.line}</p>
            <ul className="landing-items">
              {group.items.map(item => (
                <li key={item.name} className="landing-item">
                  <p className="landing-item-name">{item.name}</p>
                  <p className="landing-item-blurb">{item.blurb}</p>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <div
        className={`landing-beat landing-ready${revealed.has('beat-ready') ? ' is-in' : ''}`}
        id="beat-ready"
        data-beat="beat-ready"
        data-beat-index={BEATS.length}
      >
        <p className="landing-line landing-line-final">Pear is ready for you.</p>
        {/* Founder email 2026-10-19: the tagline sits HERE, unbolded and
            quiet, instead of leading the Pair screen below. */}
        <p className="landing-tag">Two of you. One space to remember.</p>
        <button type="button" className="landing-enter" id="btn-landing-enter" onClick={enter}>
          Step Inside
        </button>
      </div>
    </section>
  );
}
